import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, getAccountToken, getEmpToken, setToken, setEmpToken, invalidateCache, emitDataChanged } from "@/lib/api";
import { offlineClear } from "@/lib/offline";

const Ctx = createContext(null);
const USER_KEY = "pg_user";
const cachedUser = () => { try { return getEmpToken() ? JSON.parse(localStorage.getItem(USER_KEY) || "null") : null; } catch { return null; } };

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(cachedUser);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(() => !cachedUser() && !!getAccountToken());
  const [pinLocked, setPinLocked] = useState(() => !!getAccountToken() && !getEmpToken());
  const [welcome, setWelcome] = useState(null);
  const setUser = (u) => { setUserState(u); try { u ? localStorage.setItem(USER_KEY, JSON.stringify(u)) : localStorage.removeItem(USER_KEY); } catch {} };

  const refresh = useCallback(async () => {
    if (!getAccountToken()) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      if (data.pin_required) { setAccount(data.account); setPinLocked(true); } else { setUser(data); setAccount(data.account); setPinLocked(false); }
    } catch (e) { if (e.response?.status === 401) { setToken(null); setUser(null); } }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => {
    refresh();
    const onLogout = () => { setUser(null); setAccount(null); setPinLocked(false); };
    const onPin = () => { if (!getEmpToken()) setPinLocked(true); };
    window.addEventListener("pg-logout", onLogout); window.addEventListener("pg-pin-required", onPin);
    return () => { window.removeEventListener("pg-logout", onLogout); window.removeEventListener("pg-pin-required", onPin); };
  }, [refresh]);

  /** Primary account signed in (after MFA) → employee PIN still required. */
  const signIn = (payload) => { setToken(payload.access_token); setEmpToken(null); setUser(null); setAccount(payload.account); setPinLocked(true); setLoading(false); };
  /** Employee identified by PIN → attach identity to this device session. */
  const pinUnlock = (payload) => { setEmpToken(payload.access_token); setUser(payload.user); setAccount(payload.user.account); setPinLocked(false); setWelcome(payload.user.name); window.dispatchEvent(new Event("pg-pin-unlocked")); emitDataChanged(); setTimeout(() => setWelcome(null), 450); };
  /** Locks only the employee identity; the shared account stays signed in. Never discards work. */
  const lockPin = () => { const t = getEmpToken(); if (t) api.post("/auth/pin/lock", null, { headers: { Authorization: `Bearer ${t}` } }).catch(() => {}); setEmpToken(null); setPinLocked(true); };
  const signOut = () => { const t = getToken(); if (t) api.post("/auth/logout", null, { headers: { Authorization: `Bearer ${t}` } }).catch(() => {}); setToken(null); setUser(null); setAccount(null); setPinLocked(false); sessionStorage.removeItem("pg_reverify"); invalidateCache(); offlineClear(); };
  const can = (...roles) => !!user && (roles.includes(user.role) || (roles.includes("manager") && user.role === "staff" && !!user.permissions?.access_settings));

  return <Ctx.Provider value={{ user, account, loading, pinLocked, welcome, signIn, pinUnlock, lockPin, signOut, refresh, can }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
